import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const SESSION_PREFIX = 'gravion:filters:';

type FilterShape = Record<string, unknown>;

/**
 * Serializes filter state to a URL-safe search params string.
 * Arrays become comma-separated values. Empty values are omitted.
 */
function filtersToParams(filters: FilterShape, defaults: FilterShape): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    // eslint-disable-next-line security/detect-object-injection -- key comes from Object.entries of own object
    const defaultValue = defaults[key];
    // Skip values that match defaults — keep URLs short
    if (JSON.stringify(value) === JSON.stringify(defaultValue)) continue;
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      params.set(key, value.join(',')); // '' signals explicitly cleared
    } else if (value === '') {
      params.set(key, '');
    } else if (typeof value === 'object') {
      params.set(key, JSON.stringify(value));
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      params.set(key, String(value));
    }
  }
  return params;
}

/**
 * Deserializes URL search params back into a filter object.
 * Uses the defaults shape to determine types (array vs string vs object).
 */
function paramsToFilters<T>(params: URLSearchParams, defaults: T): T | null {
  const defaultsRecord = defaults as FilterShape;
  const keys = Object.keys(defaultsRecord);
  const hasAny = keys.some((key) => params.has(key));
  if (!hasAny) return null;

  const result: FilterShape = { ...(defaults as FilterShape) };
  for (const key of keys) {
    const raw = params.get(key);
    if (raw === null) continue;

    // eslint-disable-next-line security/detect-object-injection -- key from Object.keys of own object
    const defaultValue = defaultsRecord[key];
    if (Array.isArray(defaultValue)) {
      // eslint-disable-next-line security/detect-object-injection -- key from Object.keys of own object
      result[key] = raw ? raw.split(',') : [];
    } else if (defaultValue === null || typeof defaultValue === 'object') {
      try {
        // eslint-disable-next-line security/detect-object-injection -- key from Object.keys of own object
        result[key] = JSON.parse(raw) as unknown;
      } catch {
        // Malformed JSON in URL — skip this param
      }
    } else {
      // eslint-disable-next-line security/detect-object-injection -- key from Object.keys of own object
      result[key] = raw;
    }
  }
  return result as T;
}

/**
 * Reads persisted filters from sessionStorage.
 * Returns null if nothing is stored or parsing fails.
 */
function readSession<T>(pageKey: string): T | null {
  try {
    const raw = sessionStorage.getItem(SESSION_PREFIX + pageKey);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/** Writes filters to sessionStorage. */
function writeSession<T>(pageKey: string, filters: T): void {
  try {
    sessionStorage.setItem(SESSION_PREFIX + pageKey, JSON.stringify(filters));
  } catch {
    // Storage unavailable (private mode, quota). Silently ignore.
  }
}

/** Clears persisted filters from sessionStorage. */
function clearSession(pageKey: string): void {
  try {
    sessionStorage.removeItem(SESSION_PREFIX + pageKey);
  } catch {
    // Silently ignore.
  }
}

export interface UsePersistedFiltersResult<T> {
  /** Current filter state. */
  filters: T;
  /** Update one or more filter keys. Persists to session + URL. */
  setFilters: (next: T | Partial<T>) => void;
  /** Replace the entire filter state. Persists to session + URL. */
  replaceFilters: (next: T) => void;
  /** Reset to defaults. Clears session + URL params. */
  resetFilters: () => void;
}

/**
 * Shared hook for filter persistence across page navigation.
 *
 * Priority on mount:
 *   1. URL search params (shared link opened) — highest priority
 *   2. sessionStorage (returning to page within same session)
 *   3. defaults (first visit)
 *
 * On every filter change:
 *   - Writes to sessionStorage (survives in-app navigation)
 *   - Updates URL search params (Share button copies current URL with filters)
 *
 * @param pageKey Unique key for this page's filters (e.g. 'audits', 'tasks')
 * @param defaults Default filter values — also used to determine value types for URL parsing
 */
export function usePersistedFilters<T>(
  pageKey: string,
  defaults: T,
  initialOverrides?: Partial<T>,
): UsePersistedFiltersResult<T> {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  // Determine initial state: URL > session > defaults (with overrides for first visit)
  const [filters, setFiltersState] = useState<T>(() => {
    const fromUrl = paramsToFilters(searchParams, defaults);
    if (fromUrl) return fromUrl;
    const fromSession = readSession<T>(pageKey);
    if (fromSession) return fromSession;
    // First visit — apply user-specific overrides on top of neutral defaults
    return initialOverrides ? { ...defaults, ...initialOverrides } : defaults;
  });

  // Sync URL params whenever filters change (after initial mount)
  const isInitialMount = useRef(true);
  const skipNextWrite = useRef(false);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // On mount, if we loaded from session but URL is empty, push to URL
      const fromUrl = paramsToFilters(searchParams, defaultsRef.current);
      if (!fromUrl) {
        const params = filtersToParams(
          filters as FilterShape,
          defaultsRef.current as FilterShape,
        );
        if (params.toString()) {
          setSearchParams(params, { replace: true });
        }
      }
      // Persist to session on mount so URL-loaded filters survive navigation
      writeSession(pageKey, filters);
      return;
    }
    // After a reset, skip the write so neutral defaults don't overwrite
    // the cleared session (preserving initialOverrides on next visit).
    if (skipNextWrite.current) {
      skipNextWrite.current = false;
      return;
    }
    const params = filtersToParams(
      filters as FilterShape,
      defaultsRef.current as FilterShape,
    );
    setSearchParams(params, { replace: true });
    writeSession(pageKey, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when filters change
  }, [filters, pageKey, setSearchParams]);

  const setFilters = useCallback((next: T | Partial<T>) => {
    setFiltersState((prev) => ({ ...prev, ...next }));
  }, []);

  const replaceFilters = useCallback((next: T) => {
    setFiltersState(next);
  }, []);

  const resetFilters = useCallback(() => {
    skipNextWrite.current = true;
    setFiltersState(defaultsRef.current);
    clearSession(pageKey);
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [pageKey, setSearchParams]);

  return { filters, setFilters, replaceFilters, resetFilters };
}
